// src/stores/packs.js
import { defineStore } from 'pinia'
import { fbDatabase, fbFunctions } from 'src/boot/firebase'
import { deleteDbChild, incrementDbChild, setDbChild, updateDbChild } from 'src/functions/function-fb-crud'
import { Dialog } from 'quasar'
import { useUserStore } from './store-user'
import { logIt } from 'src/functions/function-logger'
import { getNextReview } from 'src/functions/function-pill-review'

// packs already reported to the dev log in this session (the getter runs often)
const reportedHiddenPacks = new Set()
// packs/data reads in flight, by pack id (see fetchPackData)
const packDataRequests = {}

// Import other stores for cross-store dispatches
import { usePillsStore } from 'src/stores/store-pills'
import { useProgressStore } from 'src/stores/store-progress'
import { useModalsStore } from 'src/stores/store-modals'

export const usePacksStore = defineStore('packs', {
  state: () => ({
    publicPacks: {},
    publicPacksData: {},
    optimisticReviewRewardProgressDeltaByPack: {},
    optimisticUniquePillPendingById: {},
    search: '',
    sort: 'toReview',
    tag: '',
    publicPacksDownloaded: false,
  }),

  getters: {
    userId() {
      return useUserStore().uid
    },
    userPacks() {
      return useProgressStore().userPacks
    },
    packsDownloaded() {
      return useProgressStore().progressDownloaded
    },
    getAllPillIds() {
      const ids = new Set()
      Object.values(this.userPacks || {}).forEach((pack) => {
        Object.keys(pack.pills || {}).forEach((id) => ids.add(id))
      })
      return Array.from(ids)
    },
    userPacksList() {
      // We need userPills from userpills store, and review stats from packs/userPacks
      const pillsStore = usePillsStore()
      const packsInfo = this.publicPacks
      const userPacks = this.userPacks
      // Deep-clone userPacks so we can add fields without mutating original
      const finalList = JSON.parse(JSON.stringify(userPacks || {}))

      Object.keys(finalList).forEach((packId) => {
        const lists = {
          new: [],
          old: [],
          due: [],
          learned: [],
          archived: [],
        }
        const counts = {
          new: 0,
          old: 0,
          due: 0,
          learned: 0,
          archived: 0,
          totalPills: 0,
          userPills: 0,
          toReview: 0,
        }

        if (packId === 'default') {
          finalList[packId].info = {
            name: 'Default Pack',
            id: 'default',
            description: 'This is your default pack, all new pills will be added here by default.',
          }
        } else if (!finalList[packId]?.info) {
          finalList[packId].info = packsInfo[packId]
          counts.totalPills = packsInfo?.[packId]?.total || 0
        }

        // The user has a pack that is not public (no info): hide it from the app and tell the dev through the log
        if (!finalList[packId].info) {
          if (this.publicPacksDownloaded && !reportedHiddenPacks.has(packId)) {
            reportedHiddenPacks.add(packId)
            logIt({ type: 'pack', text: 'user has a pack that is not public, hidden from the app', packId })
          }
          delete finalList[packId]
          return
        }

        // Count how many pills user already has in this pack
        counts.userPills = Object.keys(finalList[packId]?.pills || {}).length

        // Replace each pillId with the actual pill data from userPills store
        Object.keys(finalList?.[packId]?.pills || {}).forEach((pillId) => {
          const pillData = pillsStore.pills[pillId]
          finalList[packId].pills[pillId] = pillData
        })

        // Now categorize each user pill by its review stats
        Object.keys(finalList?.[packId]?.pills || {}).forEach((pillId) => {
          const pillStats = finalList?.[packId]?.review?.[pillId]
          const pillData = pillsStore?.pills?.[pillId]

          // Pill data may not be loaded yet if fetchPillData hasn't resolved. skip to avoid pushing undefined into lists
          if (!pillData) return

          if (pillStats) {
            const { archived, learned } = pillStats
            const nextReview = getNextReview(pillStats)
            if (learned) {
              lists.learned.push(pillData)
              counts.learned++
            } else if (archived) {
              lists.archived.push(pillData)
              counts.archived++
            } else if (nextReview) {
              if (Date.now() > nextReview) {
                lists.due.push(pillData)
                counts.due++
                counts.toReview++
              } else {
                lists.old.push(pillData)
              }
            }
          } else {
            lists.new.push(pillData)
            counts.new++
            counts.toReview++
          }
        })

        finalList[packId].lists = lists
        finalList[packId].counts = counts
      })

      return finalList
    },

    sortedPacksList() {
      const userPacksList = this.userPacksList

      const sortedList = Object.keys(userPacksList)
        .map((key) => userPacksList[key])
        .filter((pack) => {
          const packName = pack.info?.name || ''
          const packTags = Object.keys(pack.info?.tags || {})
          const searchMatch = packName.toLowerCase().includes(this.search.toLowerCase())
          const tagMatch = this.tag ? packTags.includes(this.tag) : true
          return searchMatch && tagMatch
        })
        .sort((a, b) => {
          let sortValue = 0
          if (this.sort === 'name') {
            sortValue = a.info?.name.localeCompare(b.info?.name)
          } else if (this.sort === 'new') {
            sortValue = b.counts.new - a.counts.new
          } else if (this.sort === 'due') {
            sortValue = b.counts.due - a.counts.due
          } else if (this.sort === 'toReview') {
            sortValue = b?.counts?.toReview - a?.counts?.toReview
          }
          return sortValue
        })

      return sortedList
    },

    pillsReviewStats() {
      // Collect all pill review stats from all user packs
      const allStats = {}
      const userPacksList = this.userPacksList
      Object.keys(userPacksList).forEach((packId) => {
        const pack = userPacksList[packId]
        if (pack.review) {
          Object.keys(pack.review).forEach((pillId) => {
            allStats[pillId] = pack.review[pillId]
          })
        }
      })
      return allStats
    },

    pillsActivityStats() {
      // Collect activity-specific stats (lastConcluded, lastScore, count) from activities node
      // Kept separate from review stats to avoid triggering monitorPillStudy cloud function
      // fallbackActivities: history of public-pack pills the user does not own
      const allStats = { ...(useProgressStore().progress?.fallbackActivities || {}) }
      const userPacksList = this.userPacksList
      Object.keys(userPacksList).forEach((packId) => {
        const pack = userPacksList[packId]
        if (pack.activities) {
          Object.keys(pack.activities).forEach((pillId) => {
            allStats[pillId] = pack.activities[pillId]
          })
        }
      })
      return allStats
    },
    reviewRewardProgressForPack() {
      return (packId) => {
        const base = this.userPacks[packId]?.studyRewardProgress || 0
        const optimisticDelta = this.optimisticReviewRewardProgressDeltaByPack?.[packId] || 0
        return base + optimisticDelta
      }
    },
    reviewRewardPendingForPack() {
      return (packId) => {
        return this.userPacks[packId]?.pillsToClaim || 0
      }
    },
    canClaimReviewPillFromPack() {
      return (packId) => {
        const pendingPills = this.userPacks[packId]?.pillsToClaim || 0
        return pendingPills > 0
      }
    },
  },

  actions: {
    anticipateReviewRewardProgress({ packId, pillId }) {
      if (!packId || !pillId) return false

      const progressStore = useProgressStore()
      const alreadyReviewedToday = !!progressStore?.progress?.daily?.uniquePillReviews?.[pillId]
      if (alreadyReviewedToday) return false

      if (this.optimisticUniquePillPendingById?.[pillId]) return false

      this.optimisticUniquePillPendingById[pillId] = {
        packId,
        createdAt: Date.now(),
      }

      const currentDelta = this.optimisticReviewRewardProgressDeltaByPack?.[packId] || 0
      this.optimisticReviewRewardProgressDeltaByPack[packId] = currentDelta + 1
      return true
    },
    clearOptimisticReviewRewardForPack(packId) {
      if (!packId) return

      delete this.optimisticReviewRewardProgressDeltaByPack[packId]

      Object.keys(this.optimisticUniquePillPendingById || {}).forEach((pillId) => {
        const pending = this.optimisticUniquePillPendingById[pillId]
        if (pending?.packId === packId) {
          delete this.optimisticUniquePillPendingById[pillId]
        }
      })
    },
    getPackName(packId) {
      const publicPacks = this.publicPacks
      const packName = publicPacks?.[packId]?.name || packId
      return packName || 'pill'
    },
    getPackCollectedStatus(packId) {
      let out = {}
      const pack = this.userPacksList?.[packId]
      const publicPack = this.publicPacks?.[packId]
      let userCount = pack?.counts?.userPills || 0
      let totalCount = pack?.counts?.totalPills || publicPack?.total || 0
      if (packId === 'default') {
        out.text = userCount + ' pills'
        out.percentage = 0
      } else if (totalCount > 0) {
        out.text = userCount + ' / ' + totalCount
        out.percentage = Math.round((userCount / totalCount) * 100)

        if (userCount === totalCount) {
          out.complete = true
          out.tooltip = ' You collected all pills in this pack!'
        } else {
          out.tooltip = 'You collected ' + userCount + ' out of ' + totalCount + ' pills.'
        }
      } else {
        out.text = userCount + ' pills'
        out.percentage = 0
        out.tooltip = 'This pack contains ' + totalCount + ' pills.'
      }
      out.toCollect = totalCount - userCount

      return out
    },
    // force: read again (a pack was published while the app was open, see pharmacyVersion in App.vue)
    async fetchPublicPacksInfos(force = false) {
      if (this.publicPacksDownloaded && !force) {
        return
      }

      fbDatabase
        .ref('packs/info')
        .orderByChild('public')
        .equalTo(true)
        .once('value', (snapshot) => {
          const data = snapshot.val()
          if (data) {
            Object.keys(data).forEach((key) => {
              this.publicPacks[key] = data[key]
            })
          }
          this.publicPacksDownloaded = true
        }, (error) => {
          // the boot loader waits for this flag: do not keep the app hidden when the read fails
          console.error('public packs read failed', error)
          this.publicPacksDownloaded = true
        })
    },

    async fetchPublicPackInfo(packId) {
      fbDatabase.ref('packs/info/' + packId).once(
        'value',
        (snapshot) => {
          const data = snapshot.val()
          if (data) {
            this.publicPacks[packId] = data
          }
        },
        (error) => {
        },
      )
    },

    createPack(payload) {
      const pushKey = payload.dbKey || fbDatabase.ref(`user-progress/${this.userId}/packs`).push().getKey()

      payload.data.id = pushKey
      payload.data.by = this.userId

      setDbChild({
        path: `user-progress/${this.userId}/packs/${pushKey}/info`,
        data: payload.data,
        message: 'Pack created with id: ' + pushKey,
      })

      if (payload.path) {
        const stepRef = fbDatabase.ref(`${payload.path}`)
        const stepPushKey = stepRef.push().getKey()
        stepRef.child(stepPushKey).set({
          type: 'pack',
          ref: pushKey,
          hideAnswer: true,
        })
      }
    },

    updatePack(payload) {
      updateDbChild({
        path: `user-progress/${this.userId}/packs/${payload.id}/info`,
        data: payload,
        message: 'Pack updated.',
      })
    },

    async promptToDeletePack(payload) {
      const collectedBy = await fbDatabase
        .ref(`packs/data/${payload.id}`)
        .once('value')
        .then((snapshot) => snapshot.val())

      if (collectedBy) {
        return new Promise((resolve) => {
          Dialog.create({
            title: 'Delete this pack?',
            message: `This pack has ${Object.keys(collectedBy).length} pills associated. Delete only references?`,
            ok: { push: true },
            cancel: { color: 'negative' },
          })
            .onOk(() => {
              Object.keys(collectedBy).forEach(() => {
                deleteDbChild({
                  path: `packs/data/${payload.id}`,
                  message: `Pack data deleted.`,
                })
              })
              this.deletePack(payload)
              resolve('success')
            })
            .onCancel(() => {
              resolve('cancelled')
            })
            .onDismiss(() => {
              resolve('dismissed')
            })
        })
      } else {
        this.deletePack(payload)
        return 'success'
      }
    },
    deletePack(payload) {
      deleteDbChild({
        path: `user-progress/${this.userId}/packs/${payload.id}`,
        message: 'Pack deleted.',
      })
    },

    // a pack's pill ids, read once per session; a call while that pack is loading waits for the same read
    fetchPackData(packId) {
      if (this.publicPacksData[packId]) return Promise.resolve(this.publicPacksData[packId])
      if (!packDataRequests[packId]) {
        packDataRequests[packId] = fbDatabase
          .ref('packs/data/' + packId)
          .once('value')
          .then((snapshot) => {
            const pills = snapshot.val()
            if (pills) this.publicPacksData[packId] = pills
            return pills
          })
          .finally(() => delete packDataRequests[packId])
      }
      return packDataRequests[packId]
    },

    // preload (Pharmacy, today's daily pill): the checkout then opens without waiting for the db,
    // which sometimes takes seconds to answer the first read of a pack
    preloadPacksData(packIds) {
      packIds.forEach((packId) => {
        this.fetchPackData(packId).catch((error) => console.error('pack data preload failed', packId, error))
      })
    },

    async newPillsFromThisPack({ packId, count = 1, eager = false }) {
      const userPillIds = Object.keys(this.userPacksList?.[packId]?.pills || {})

      const storePills = await this.fetchPackData(packId)

      if (!storePills) {
        console.error('newPillsFromThisPack - no pills found')
        return { error: 'no pills found' }
      }

      const selectedPillIds = []
      Object.keys(storePills).some((pillId) => {
        if (!userPillIds.includes(pillId) && selectedPillIds.length < count) {
          selectedPillIds.push(pillId)
        }
      })

      if (selectedPillIds.length < count) {
        console.error('newPillsFromThisPack - not enough found')
        return { error: 'not enough pills' }
      }

      if (eager) {
        selectedPillIds.forEach((pillId) => {
          useProgressStore().collectPill({
            pillId,
            packId,
            source: 'no source',
          })
        })
      }

      return selectedPillIds
    },

    async purchasePills({ packId, count = 1, promoCode = null }) {
      const newPillIds = await this.newPillsFromThisPack({
        packId,
        count,
      })
      if (!newPillIds || newPillIds.error || newPillIds.length === 0) {
        console.error('purchasePills: no new pills found', { packId, result: newPillIds })
        return
      }

      const payload = {
        type: 'pill',
        subtype: packId,
        itemIds: newPillIds,
        total: 250 * newPillIds.length,
        promoCode,
      }
      useModalsStore().checkoutModal = payload
    },

    async claimStarterPack({ packId }) {
      const newPillIds = await this.newPillsFromThisPack({ packId, count: 10 })
      if (!newPillIds || newPillIds.error || newPillIds.length === 0) {
        return { error: newPillIds?.error || 'no pills found' }
      }

      useModalsStore().checkoutModal = {
        type: 'pill',
        itemIds: newPillIds,
        packId,
        promoCode: 'starter_pack',
        message: 'Get 10 starter pills free!',
      }
    },

    async fetchSamplePills({ packId, count = 3 }) {
      const storePills = await this.fetchPackData(packId)
      if (!storePills) return []
      return Object.keys(storePills).slice(0, count)
    },

    /**
     * Returns suggested pack IDs for the given English level.
     * Used by activity fallback selection and tutorial pack picker.
     * @param {string|null} level 'beginner' | 'intermediate' | 'advanced' | null
     * @returns {string[]}
     */
    getSuggestedPackIds(level) {
      if (level === 'intermediate') return ['phrasal', 'proverb', 'adj', 'idiom', 'silent', 'abbrev']
      if (level === 'advanced') return ['quote', 'riddle', 'Conjunctions', 'chemElem', 'science']
      return ['verb', 'Colors', 'Numbers', 'symbol', 'Animals', 'Numbers_Ordinal']
    },
  },
})
